import { Component, ElementRef, Input, OnInit, Renderer2, ViewChild } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { ModalController } from "@ionic/angular";
import { LoveItAlertService } from "../../services/love-it-alert.service";
import { OidcSecurityService } from "angular-auth-oidc-client";
import { getCleanedHtml } from "../../../../quill-languagetool";
import { DetailAktDataRetrieveResponse } from "../../../api/models/detail-akt-data-retrieve-response";
import { Kontakt } from "../../../api/models/kontakt";
import { KontaktDetail } from "../../../api/models/kontakt-detail";
import { MimeAttachment } from "../../../api/models/mime-attachment";
import { TextvorlagenService } from "../../../api/services/textvorlagen.service";
import { TextvorlageComponent } from "../../../textvorlage/textvorlage.component";
import {
  EmpfaengerGruppe,
  KundenterminStatus,
  TerminBerichtUrgentStatus,
  TerminRueckfrageAnStatus,
  TextbausteinPlatzhalter,
  UserRole,
  ToastType,
} from "../../enums/global-enums";
import { getKontaktBezeichnungByKuerzel, translateQuillEditorElements } from "../../global-functions/global-functions";
import { buildQuillEditorModules } from "../../quill-editor-config";
import { AuthService } from "../../services/auth.service";
import { DownloadService } from "../../services/download.service";
import { EmailService } from "../../services/email.service";
import { LoveItToastService } from "../../services/love-it-toast.service";
import { KontaktdatenAnsichtModalComponent } from "../kontaktdaten-ansicht-modal/kontaktdaten-ansicht-modal.component";
import { FileViewerComponent } from "../pdf-viewer/file-viewer.component";

export interface EmailRecipient {
  empfaengerGruppe: EmpfaengerGruppe;
  name: string;
  emailAddress: string;
  required: boolean;
  person?: KontaktDetail;
}

export interface MailData {
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  message: string;
  attachments?: MimeAttachment[];
  date: string;
}

@Component({
  selector: "app-compose-email",
  templateUrl: "./compose-email.component.html",
  styleUrls: ["./compose-email.component.scss"],
  standalone: false,
})
export class ComposeEmailComponent implements OnInit {
  @Input({ required: true }) aktId: number;
  @Input({ required: true }) aktTitle: number;
  @Input({ required: true }) defaultSubject: string;
  @Input({ required: true }) setRecipientToBackoffice: boolean;
  @Input({ required: true }) details: DetailAktDataRetrieveResponse;
  @Input({ required: true }) submitMessage: string;
  @Input({ required: true }) terminStatus: KundenterminStatus | TerminRueckfrageAnStatus | TerminBerichtUrgentStatus;
  @Input({ required: true }) date: Date;
  @Input({ required: true }) formattedTime: string;
  @Input({ required: true }) callAfterSubmit: (mail: MailData) => Promise<void>;
  @Input() includeVUSBRecipient: boolean = true;
  @Input() denyAdditionalRecipients: boolean = false;
  @Input() additionalDefaultRecipients: EmailRecipient[] = [];
  @Input() defaultMailBody: string;
  @Input() rueckstellung: number;

  @ViewChild("fileupload") fileupload: ElementRef;

  isSaving: boolean = false;
  submitted: boolean = false;
  emailVersendenFormGroup: UntypedFormGroup;
  attachments: MimeAttachment[] = [];
  kontakteDropdownOptions: { value: KontaktDetail; text: string }[] = [];
  readonly backofficeName: string = "Backoffice";
  readonly backofficeEmail: string = "office@faircheck.at";
  readonly textbausteinKategorie: string = "E-Mail";
  readonly defaultBackofficeDropdownOption: {
    value: KontaktDetail;
    text: string;
  } = {
    value: {
      Id: null,
      Email: this.backofficeEmail,
      Name: this.backofficeName,
    },
    text: `${this.backofficeName} | ${this.backofficeEmail} | FCA`,
  };
  quillEditorModules: any;

  constructor(
    private modalController: ModalController,
    private oidcSecurityService: OidcSecurityService,
    private loveItAlertService: LoveItAlertService,
    private fb: FormBuilder,
    private loveItToastService: LoveItToastService,
    private authService: AuthService,
    private textvorlagenService: TextvorlagenService,
    private emailService: EmailService,
    protected downloadService: DownloadService,
    protected renderer: Renderer2,
  ) {}

  ngOnInit() {
    this.quillEditorModules = buildQuillEditorModules(this.oidcSecurityService.getToken());

    if (this.details.Contacts?.FCA?.Email === this.backofficeEmail) {
      this.defaultBackofficeDropdownOption.value = this.details.Contacts?.FCA;
      this.defaultBackofficeDropdownOption.value.Name = this.backofficeName;
    }

    const defaultRecipients: EmailRecipient[] = [];

    if (this.setRecipientToBackoffice) {
      defaultRecipients.push({
        empfaengerGruppe: EmpfaengerGruppe.to,
        name: `${
          this.defaultBackofficeDropdownOption.value.Vorname
            ? this.defaultBackofficeDropdownOption.value.Vorname + " "
            : ""
        }${this.defaultBackofficeDropdownOption.value.Name ?? ""}`,
        emailAddress: this.defaultBackofficeDropdownOption.value.Email,
        required: true,
        person: this.defaultBackofficeDropdownOption.value,
      } as EmailRecipient);
    }

    if (this.details.Contacts?.VUSB?.Email && this.includeVUSBRecipient) {
      defaultRecipients.push({
        empfaengerGruppe: EmpfaengerGruppe.to,
        name: `${
          this.details.Contacts?.VUSB?.Vorname ? this.details.Contacts?.VUSB?.Vorname + " " : ""
        }${this.details.Contacts?.VUSB?.Name ?? ""}`,
        emailAddress: this.details.Contacts?.VUSB?.Email,
        required: true,
        person: this.details.Contacts?.VUSB,
      } as EmailRecipient);
    }

    this.additionalDefaultRecipients.forEach((recipient) => {
      if (!defaultRecipients.some((defaultRecipient) => defaultRecipient.emailAddress === recipient.emailAddress)) {
        defaultRecipients.push(recipient);
      }
    });

    this.emailVersendenFormGroup = this.fb.group({
      emailBetreff: [this.defaultSubject, Validators.required],
      emailText: [null, Validators.required],
      emailRecipients: new FormControl<EmailRecipient[]>(defaultRecipients, this.recipientsValidator()),
    });
    this.emailVersendenFormGroup.disable();
    this.textvorlagenService
      .textvorlagenRetrieveFirstList({
        benutzer: this.oidcSecurityService.getPayloadFromIdToken().sb,
        kategorie: this.textbausteinKategorie.toLowerCase(),
        zuordnung: this.terminStatus?.toString(),
      })
      .subscribe({
        next: (response) => {
          this.emailVersendenFormGroup
            .get("emailText")
            .setValue(this.replacePlatzhalterForString(response?.inhalt) + (this.defaultMailBody ?? ""));

          this.emailVersendenFormGroup.enable();
        },
        error: (error) => {
          this.emailVersendenFormGroup.enable();
        },
      });
    this.loadKontaktDropdownOptions();
  }

  cancelButton() {
    if (this.emailVersendenFormGroup.dirty) {
      this.abbrechenBestaetigenDialogAnzeigen();
    } else {
      this.modalController.dismiss(null, "cancel");
    }
  }

  async submit() {
    this.submitted = true;
    this.validateEmailRecipientsArray();
    if (this.emailVersendenFormGroup.invalid) {
      this.emailVersendenFormGroup.markAllAsTouched();
      return;
    }

    const emailBetreff = this.emailVersendenFormGroup.get("emailBetreff")?.value;
    const emailText = getCleanedHtml(this.emailVersendenFormGroup.get("emailText")?.value ?? "");
    const emailAttachments = this.attachments;
    const emailRecipients: EmailRecipient[] = this.emailVersendenFormGroup.get("emailRecipients")?.value;
    const to = emailRecipients
      .filter((r) => r.empfaengerGruppe == EmpfaengerGruppe.to)
      .map((r) => this.getRecipientEmailWithName(r))
      .join(", ");
    const cc = emailRecipients
      .filter((r) => r.empfaengerGruppe == EmpfaengerGruppe.cc)
      .map((r) => this.getRecipientEmailWithName(r))
      .join(", ");
    const bcc = emailRecipients
      .filter((r) => r.empfaengerGruppe == EmpfaengerGruppe.bcc)
      .map((r) => this.getRecipientEmailWithName(r))
      .join(", ");

    const date = new Date().toUTCString();

    const mailData: MailData = {
      to: to,
      cc: cc,
      bcc: bcc,
      subject: emailBetreff,
      message: emailText,
      attachments: emailAttachments,
      date: date,
    };

    const alert = await this.loveItAlertService.create({
      header: "E-Mail versenden?",
      message: this.submitMessage,
      buttons: [
        {
          text: "Abbrechen",
          role: "cancel",
          cssClass: "color-primary",
        },
        {
          cssClass: "color-secondary",
          text: "Senden",
          handler: async () => {
            this.isSaving = true;
            this.callAfterSubmit(mailData)
              .then(() => {
                this.isSaving = false;
                this.modalController.dismiss(null, "confirm");
              })
              .catch(async (error) => {
                this.isSaving = false;
                this.loveItToastService.showToastMessage(error.message, ToastType.Error);
              });
          },
        },
      ],
    });

    await alert.present();
  }

  async openPersonAnsichtModal(person: KontaktDetail) {
    const modal = await this.modalController.create({
      component: KontaktdatenAnsichtModalComponent,
      cssClass: [],
      componentProps: {
        titel: "",
        enableEdit: false,
        anspruchsteller: person.Name,
        auftrag: this.details.Title,
        schadensnummer: this.details.Incident_number,
        fca: this.details.Contacts?.FCA,
        instance: person,
        akt_id: this.aktId,
      },
    });

    await modal.present();

    const { data, role } = await modal.onWillDismiss();
  }

  async openTextbausteinModal(formControl, field: string) {
    const modal = await this.modalController.create({
      component: TextvorlageComponent,
      cssClass: ["resizeable-modal", "textvorlage-modal-size"],
      backdropDismiss: false,
      componentProps: {
        content: formControl.value,
        field,
        allowTextbausteinEditing: this.authService.hasCurrentUserAccessForRole(UserRole.BackofficeAdmin),
        zuordnung: this.terminStatus?.toString(),
      },
    });

    modal.onDidDismiss().then((data) => {
      const d = data.data;
      if (d && d.keep) {
        formControl.patchValue(this.replacePlatzhalterForString(d.content));
        formControl.markAsDirty();
      }
    });

    await modal.present();
  }

  get emailRecipientArray(): EmailRecipient[] {
    return this.emailVersendenFormGroup.get("emailRecipients").value as EmailRecipient[];
  }

  recipientsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const errors: ValidationErrors = {};
      if (control.value.length == 0) {
        return {
          customError: "Kein Empfänger eingetragen!",
        };
      }

      for (let i = 0; i < control.value.length; i++) {
        const entry: EmailRecipient = control.value[i];
        const currentKey: string = `customError${i}`;

        if (entry.emailAddress === "") {
          errors[currentKey] = "E-Mail Adresse darf nicht leer sein!";
          continue;
        }

        const emailRegex = this.emailService.getEmailValidationRegex();
        const isValidEmail = emailRegex.test(entry.emailAddress);
        if (!isValidEmail) {
          errors[currentKey] = "Ungültige E-Mail Adresse!";
          continue;
        }

        if (isValidEmail && !entry.empfaengerGruppe) {
          errors[currentKey] = "Empfängergruppe für E-Mail nicht gesetzt!";
          continue;
        }
      }

      return Object.keys(errors).length > 0 ? errors : null;
    };
  }

  validateEmailRecipientsArray() {
    this.emailVersendenFormGroup.get("emailRecipients").updateValueAndValidity();
  }

  addRecipient() {
    this.emailRecipientArray.push({
      empfaengerGruppe: EmpfaengerGruppe.to,
      emailAddress: "",
      required: false,
      person: null,
    } as EmailRecipient);
    this.validateEmailRecipientsArray();
  }

  setEmpfaengergruppeForRecipientByIndex(index: number, empfaengerGruppe: EmpfaengerGruppe) {
    this.emailRecipientArray[index].empfaengerGruppe = empfaengerGruppe;
    this.validateEmailRecipientsArray();
  }

  editNameForRecipientByIndex(index: number, newName: string) {
    this.emailRecipientArray[index].name = newName;
    this.validateEmailRecipientsArray();
  }

  editEmailAddressForRecipientByIndex(index: number, newEmail: string) {
    this.emailRecipientArray[index].emailAddress = newEmail;
    this.validateEmailRecipientsArray();
  }

  removeRecipientByIndex(index: number) {
    this.emailRecipientArray.splice(index, 1);
    this.validateEmailRecipientsArray();
    this.loadKontaktDropdownOptions();
  }

  removeAttachmentByIndex(index: number) {
    this.attachments.splice(index, 1);
  }

  async abbrechenBestaetigenDialogAnzeigen() {
    const alert = await this.loveItAlertService.create({
      header: "Ungespeicherte Änderungen vorhanden",
      message: "Wollen Sie alle Änderungen an der E-Mail verwerfen und das Fenster schließen?",
      buttons: [
        {
          cssClass: "color-primary",
          text: "Abbrechen",
          role: "cancel",
        },
        {
          cssClass: "color-danger",
          text: "Verwerfen",
          handler: () => {
            this.modalController.dismiss(null, "cancel");
          },
        },
      ],
    });
    await alert.present();
  }

  getRecipientEmailWithName(recipient: EmailRecipient) {
    const escapeDisplayName = (name: string): string =>
      name.replace(/\\/g, "\\\\").replace(/"/g, '\\"').replace(/\r/g, "").replace(/\n/g, "");
    if (recipient.name) {
      return `"${escapeDisplayName(recipient.name)}" <${recipient.emailAddress}>`;
    } else {
      return recipient.emailAddress;
    }
  }

  isKontaktDetail(value: any): value is KontaktDetail {
    return value && typeof value.Name === "string" && typeof value.Email === "string";
  }

  loadKontaktDropdownOptions() {
    this.kontakteDropdownOptions = [];
    let backofficeExists = false;
    for (const key in this.details.Contacts) {
      const value: KontaktDetail = this.details.Contacts[key as keyof Kontakt];
      if (this.isKontaktDetail(value) && value.Email) {
        if (value.Email === this.backofficeEmail) {
          backofficeExists = true;
          value.Name = this.backofficeName;
        }
        this.kontakteDropdownOptions.push({
          value: value,
          text: `${value.Vorname ? value.Vorname + " " : ""}${
            value.Name ?? ""
          }${value.Email ? " | " + value.Email : ""}${
            getKontaktBezeichnungByKuerzel(key) ? " | " + getKontaktBezeichnungByKuerzel(key) : ""
          }`,
        });
      }
    }
    const index = this.kontakteDropdownOptions.findIndex((option) => option.value.Email === this.backofficeEmail);

    if (index !== -1) {
      const [foundOption] = this.kontakteDropdownOptions.splice(index, 1);
      this.kontakteDropdownOptions = [foundOption, ...this.kontakteDropdownOptions];
    }
    if (!backofficeExists) {
      this.kontakteDropdownOptions = [this.defaultBackofficeDropdownOption, ...this.kontakteDropdownOptions];
    }
    const recipients: EmailRecipient[] = this.emailVersendenFormGroup?.get("emailRecipients").value as EmailRecipient[];
    this.kontakteDropdownOptions = this.kontakteDropdownOptions.filter(
      (option) => !recipients.map((recipient) => recipient.emailAddress).includes(option.value.Email),
    );
  }

  kontakteDropdownChanged(value: KontaktDetail, selectComponent) {
    this.emailVersendenFormGroup.get("emailRecipients").value.push({
      empfaengerGruppe: EmpfaengerGruppe.to,
      name: `${value.Vorname ? value.Vorname + " " : ""}${value.Name ?? ""}`,
      emailAddress: value.Email,
      required: false,
      person: value,
    } as EmailRecipient);
    this.loadKontaktDropdownOptions();
    selectComponent.value = null;
    this.validateEmailRecipientsArray();
  }

  replacePlatzhalterForString(text: string): string {
    if (this.date && this.formattedTime) {
      text = text.replace(
        new RegExp(TextbausteinPlatzhalter.Besichtigungstermin, "g"),
        `${new Date(this.date).toLocaleDateString("de-DE", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })} gegen ${this.formattedTime} Uhr (+- 30min.)`,
      );
    } else {
      text = text.replace(new RegExp(TextbausteinPlatzhalter.Besichtigungstermin, "g"), ``);
    }

    const kundenprozessLink: string = `https://fairapp.faircheck.at/kundenprozess?aktid=${this.aktId}&amp;snr=${this.details.Incident_number}`;
    text = text.replace(
      new RegExp(TextbausteinPlatzhalter.KundenprozessLink, "g"),
      `<a href="${kundenprozessLink}" rel="noopener noreferrer" target="_blank">${kundenprozessLink}</a>`,
    );

    const rueckstellung = this.rueckstellung;
    if (rueckstellung) {
      const rueckstellungNumber = +rueckstellung;
      const rueckstellungFormatted = rueckstellungNumber.toLocaleString("de");
      text = text.replace(new RegExp(TextbausteinPlatzhalter.Rueckstellung, "g"), `${rueckstellungFormatted}`);
    }

    text = text.replace(new RegExp("\n", "g"), `<br>`);

    return text;
  }

  addAttachments() {
    this.fileupload.nativeElement.click();
  }

  onAttachmentsSelected(event: any) {
    const files: FileList = event.target.files;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(",")[1];

        this.attachments.push({
          file_name: file.name,
          file_bytes: base64,
        });
      };
      reader.readAsDataURL(file);
    }
    event.target.value = null;
  }

  async openDocumentViewerModal(attachment: MimeAttachment) {
    const modal = await this.modalController.create({
      component: FileViewerComponent,
      cssClass: "modal-fullscreen",
      backdropDismiss: true,
      componentProps: {
        fileSource: attachment.file_bytes,
        fileName: attachment.file_name,
        edit: true,
      },
    });
    modal.onDidDismiss().then((data) => {
      const d = data.data;
      if (data?.role === "confirm" && d) {
        this.downloadService
          .blobToBase64(d)
          .then((base64) => {
            attachment.file_bytes = base64;
          })
          .catch((error) => {
            console.error(error);
          });
      }
    });

    await modal.present();
  }

  protected readonly EmpfaengerGruppe = EmpfaengerGruppe;
  protected readonly translateQuillEditorElements = translateQuillEditorElements;
}
